// Consultas e gravação das pendências de quem sai da empresa — as regras estão
// em pendenciasUsuario.ts.
//
// A transferência roda pelo navegador, em lotes, sem transação: se cair no meio,
// o que já passou fica com o novo dono e o erro diz quanto foi. Rodar de novo é
// seguro — cada passo só pega o que ainda está no nome de quem saiu.
//
// Efeitos dos gatilhos do banco (os mesmos da transferência em massa do CRM):
// cada tarefa e cada negociação gera um aviso no sino do novo dono, e cada
// negociação abre um período novo de SLA para ele (negociacao_atribuicoes).

import { supabase } from './supabase';
import { buscarTudoPorChave } from './paginacaoKeyset';
import {
  STATUS_NEGOCIACAO_EM_ABERTO, STATUS_SOLICITACAO_ENCERRADA, STATUS_TAREFA_ENCERRADA,
  type Pendencias, type VinculosExclusao,
} from './pendenciasUsuario';

const listaPostgrest = (valores: readonly string[]) => `(${valores.join(',')})`;

const LOTE = 200;

function emLotes<T>(itens: T[], tamanho: number): T[][] {
  const lotes: T[][] = [];
  for (let i = 0; i < itens.length; i += tamanho) lotes.push(itens.slice(i, i + tamanho));
  return lotes;
}

function contagem(res: { count: number | null; error: { message: string } | null }): number {
  if (res.error) throw new Error(res.error.message);
  return res.count || 0;
}

export async function contarPendencias(userId: string): Promise<Pendencias> {
  const [tarefas, negociacoes, solicitacoes] = await Promise.all([
    supabase.from('tarefas').select('id', { count: 'exact', head: true })
      .eq('responsavel_id', userId)
      .eq('categoria', 'tarefa')
      .not('status', 'in', listaPostgrest(STATUS_TAREFA_ENCERRADA)),
    supabase.from('negociacoes').select('id', { count: 'exact', head: true })
      .eq('responsavel_id', userId)
      .in('status', [...STATUS_NEGOCIACAO_EM_ABERTO]),
    supabase.from('tarefas').select('id', { count: 'exact', head: true })
      .eq('responsavel_id', userId)
      .eq('categoria', 'solicitacao')
      .not('solicitacao_status', 'in', listaPostgrest(STATUS_SOLICITACAO_ENCERRADA)),
  ]);
  return {
    tarefas: contagem(tarefas),
    negociacoes: contagem(negociacoes),
    solicitacoes: contagem(solicitacoes),
  };
}

/** Mesmas contagens que a edge manage-users faz antes de aceitar excluir. */
export async function contarVinculosExclusao(userId: string): Promise<VinculosExclusao> {
  const [negociacoes, tarefas, notas, logsCrm, subordinados, comentarios, logsSistema] = await Promise.all([
    supabase.from('negociacoes').select('id', { count: 'exact', head: true }).eq('responsavel_id', userId),
    supabase.from('tarefas').select('id', { count: 'exact', head: true }).eq('responsavel_id', userId),
    supabase.from('negociacao_notas').select('id', { count: 'exact', head: true }).eq('user_id', userId),
    supabase.from('negociacao_logs').select('id', { count: 'exact', head: true }).eq('user_id', userId),
    supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('parceiro_id', userId),
    supabase.from('comentarios_tarefas').select('id', { count: 'exact', head: true }).eq('user_id', userId),
    supabase.from('system_logs').select('id', { count: 'exact', head: true }).eq('user_id', userId),
  ]);
  return {
    negociacoes: contagem(negociacoes),
    tarefas: contagem(tarefas),
    notas: contagem(notas),
    logsCrm: contagem(logsCrm),
    subordinados: contagem(subordinados),
    comentarios: contagem(comentarios),
    logsSistema: contagem(logsSistema),
  };
}

export interface ResultadoTransferencia {
  tarefas: number;
  negociacoes: number;
}

interface ParametrosTransferencia {
  de: { id: string; nome: string };
  para: { id: string; nome: string };
  /** Quem está fazendo a transferência — vai no histórico da negociação. */
  atorId: string;
}

/**
 * Passa as tarefas pendentes e as negociações em aberto de quem saiu para
 * `para`. Solicitações da Central ficam de fora (são redirecionadas por lá).
 */
export async function transferirPendencias({ de, para, atorId }: ParametrosTransferencia): Promise<ResultadoTransferencia> {
  const feito: ResultadoTransferencia = { tarefas: 0, negociacoes: 0 };

  const falhou = (etapa: string, mensagem: string) => {
    const jaFoi = feito.tarefas + feito.negociacoes > 0
      ? ` Já passaram para ${para.nome}: ${feito.tarefas} tarefa(s) e ${feito.negociacoes} negociação(ões). Tente de novo para levar o resto.`
      : '';
    return new Error(`Falha ao transferir ${etapa}: ${mensagem}.${jaFoi}`);
  };

  // 1. Tarefas e visitas pendentes — um comando só, filtrado no banco.
  const { data: tarefas, error: erroTarefas } = await supabase
    .from('tarefas')
    .update({ responsavel_id: para.id })
    .eq('responsavel_id', de.id)
    .eq('categoria', 'tarefa')
    .not('status', 'in', listaPostgrest(STATUS_TAREFA_ENCERRADA))
    .select('id');
  if (erroTarefas) throw falhou('as tarefas', erroTarefas.message);
  feito.tarefas = tarefas?.length || 0;

  // 2. Negociações em aberto — precisa dos ids para registrar no histórico de cada uma.
  let ids: number[];
  try {
    const abertas = await buscarTudoPorChave<{ id: number }>(
      (apos, tamanho) => {
        let q = supabase
          .from('negociacoes')
          .select('id')
          .eq('responsavel_id', de.id)
          .in('status', [...STATUS_NEGOCIACAO_EM_ABERTO])
          .order('id', { ascending: true })
          .limit(tamanho);
        if (apos != null) q = q.gt('id', apos);
        return q as any;
      },
      (n) => n.id,
    );
    ids = abertas.map(n => n.id);
  } catch (e: any) {
    throw falhou('as negociações', e?.message || String(e));
  }

  for (const lote of emLotes(ids, LOTE)) {
    // O filtro por responsável e status de novo evita levar uma negociação que
    // mudou de mãos ou foi fechada entre a busca e a gravação.
    const { data: movidas, error } = await supabase
      .from('negociacoes')
      .update({ responsavel_id: para.id })
      .in('id', lote)
      .eq('responsavel_id', de.id)
      .in('status', [...STATUS_NEGOCIACAO_EM_ABERTO])
      .select('id');
    if (error) throw falhou('as negociações', error.message);

    const movidasIds = (movidas || []).map((m: { id: number }) => m.id);
    feito.negociacoes += movidasIds.length;

    if (movidasIds.length > 0) {
      const { error: erroLog } = await supabase.from('negociacao_logs').insert(
        movidasIds.map(id => ({
          negociacao_id: id,
          user_id: atorId,
          acao: `transferiu o responsável para ${para.nome} (saída de ${de.nome})`,
          detalhes: JSON.stringify({ previous: de.id, new: para.id, motivo: 'saida_usuario' }),
        })),
      );
      // O dono já mudou; perder a linha do histórico não justifica parar o resto.
      if (erroLog) console.warn('Histórico da transferência não gravado:', erroLog.message);
    }
  }

  return feito;
}
